import {forwardRef, type CSSProperties, type ElementType, type Ref} from "react";
import {cn} from "../lib/utils";
import {responsive, splitSpacing, type ResponsiveValue, type Spacing, type SpacingProps} from "../lib/responsive";
import {splitBorder, type BorderProps} from "../lib/border";
import {stateful, type PaletteColor, type StatefulValue} from "../lib/color";
import type {Testable} from "../lib/testable";
import type {PolymorphicComponent, PolymorphicProps} from "../lib/polymorphic";

const GridInner = forwardRef(<E extends ElementType = "div">(props: GridProps<E>, ref: Ref<Element>) => {
	const {className: spacingClassName, rest: withoutSpacing} = splitSpacing(props);
	const {className: borderClassName, rest} = splitBorder(withoutSpacing);
	const {
		as,
		columns,
		templateColumns,
		gap,
		rowGap,
		columnGap,
		alignItems,
		justifyItems,
		alignContent,
		justifyContent,
		fullWidth = false,
		queryContainer = false,
		testId,
		className,
		style,
		children,
		...domProps
	} = rest;

	// One cast so the JSX below stays readable — E is only knowable at the call site.
	const Component = (as ?? "div") as ElementType;

	return (
		<Component
			ref={ref}
			data-component="Grid"
			data-testid={testId}
			className={cn(
				"grid",
				queryContainer && "@container",
				responsive(columns, value => `grid-cols-${value}`),
				responsive(gap, value => `gap-${value}`),
				responsive(rowGap, value => `gap-y-${value}`),
				responsive(columnGap, value => `gap-x-${value}`),
				responsive(alignItems, value => `items-${value}`),
				responsive(justifyItems, value => `justify-items-${value}`),
				responsive(alignContent, value => `content-${value}`),
				responsive(justifyContent, value => `justify-${value}`),
				spacingClassName,
				borderClassName,
				fullWidth && "w-full",
				className
			)}
			style={templateColumns ? {gridTemplateColumns: templateColumns, ...style as CSSProperties} : style}
			{...domProps}>
			{children}
		</Component>
	);
});

export const Grid = GridInner as PolymorphicComponent<GridOwnProps, "div">;

Grid.displayName = "Grid";

const GridItemInner = forwardRef(<E extends ElementType = "div">({
	as,
	colSpan,
	rowSpan,
	colStart,
	backgroundColor,
	testId,
	className,
	children,
	...props
}: GridItemProps<E>, ref: Ref<Element>) => {
	const Component = (as ?? "div") as ElementType;

	return (
		<Component
			ref={ref}
			data-component="GridItem"
			data-testid={testId}
			className={cn(
				responsive(colSpan, value => `col-span-${value}`),
				responsive(rowSpan, value => `row-span-${value}`),
				responsive(colStart, value => `col-start-${value}`),
				stateful(backgroundColor, value => `bg-${value}`),
				className
			)}
			{...props}>
			{children}
		</Component>
	);
});

export const GridItem = GridItemInner as PolymorphicComponent<GridItemOwnProps, "div">;

GridItem.displayName = "GridItem";

export type GridColumns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export type GridColSpan = GridColumns | "full";

export type GridRowSpan = 1 | 2 | 3 | 4 | 5 | 6 | "full";

export type GridColStart = GridColumns | 13;

export type GridAlignItems = "start" | "center" | "end" | "stretch" | "baseline";

export type GridJustifyItems = "start" | "center" | "end" | "stretch";

export type GridContentDistribution = "start" | "center" | "end" | "between" | "around" | "evenly" | "stretch";

export interface GridOwnProps extends SpacingProps, BorderProps, Testable {
	columns?: ResponsiveValue<GridColumns>;
	/**
	 * Raw `grid-template-columns`, set on the inline style — anything the CSS property accepts:
	 * `repeat(auto-fit, minmax(12rem, 1fr))`, `auto 2fr 1fr`, named lines. Being a style rather
	 * than a class is also why it beats `columns` when both are set.
	 *
	 * The one prop here with no breakpoint object: Tailwind can't see a `grid-cols-[…]` built at
	 * runtime and the safelist can't enumerate arbitrary tracks, so responsive tracks would take
	 * CSS custom properties and a hand-written media-query block. `repeat(auto-fit, …)` usually
	 * removes the need — it reflows on width by itself.
	 */
	templateColumns?: string;
	gap?: ResponsiveValue<Spacing>;
	rowGap?: ResponsiveValue<Spacing>;
	columnGap?: ResponsiveValue<Spacing>;
	alignItems?: ResponsiveValue<GridAlignItems>;
	justifyItems?: ResponsiveValue<GridJustifyItems>;
	alignContent?: ResponsiveValue<GridContentDistribution>;
	justifyContent?: ResponsiveValue<GridContentDistribution>;
	fullWidth?: boolean;
	queryContainer?: boolean;
}

export type GridProps<E extends ElementType = "div"> = PolymorphicProps<E, GridOwnProps>;

export interface GridItemOwnProps extends Testable {
	colSpan?: ResponsiveValue<GridColSpan>;
	rowSpan?: ResponsiveValue<GridRowSpan>;
	colStart?: ResponsiveValue<GridColStart>;
	backgroundColor?: StatefulValue<PaletteColor>;
}

export type GridItemProps<E extends ElementType = "div"> = PolymorphicProps<E, GridItemOwnProps>;
